import React from 'react';
import { Info, CheckCircle2, ShieldCheck, HelpCircle } from 'lucide-react';

/**
 * Sprint S4 · Confidence banner chip (2026-02c).
 *
 * Renders the S2 3-source resolver's `(source, confidence)` tuple as a
 * compact yellow / green / blue / grey chip so the appraiser can see
 * where a m² / perimeter value came from at a glance.
 *
 * Usage:
 *   <ConfidenceBanner source={dims.appraisal_roof_details_m2_source}
 *                     confidence={dims.appraisal_roof_details_m2_confidence} />
 *
 * Tiers:
 *   estimated       · yellow · pre-visit / verbal-pitch fallback
 *   confirmed       · green  · site-measured or direct roof_m² read
 *   order_verified  · blue   · post-order per-face verified (S6)
 *   unknown         · grey   · nothing captured yet
 */

const SOURCE_LABELS = {
  face_pins:              'Pinned face m² · summed on diagram',
  sheets_components:      'Sheet rows · Σ(qty × length × cover) — actual order',
  confirmed_measurement:  'Confirmed measurement · roof_m² field',
  perimeter_pins:         'Pinned perimeter · summed on diagram',
  measured_perimeter:     'Measured perimeter length · roof_details',
  estimated_from_floor:   'Estimated · floor m² × slope factor',
  floor_only:             'Floor m² only · treated as flat (no pitch)',
  operator_override:      'Operator override · manual entry',
  none:                   'No source captured yet',
  spouting_pins:          'Source: diagram pins · total length',
};

const TIERS = {
  order_verified: {
    ring: 'ring-blue-200', bg: 'bg-blue-50', text: 'text-blue-800',
    label: 'Order-verified', icon: ShieldCheck,
  },
  confirmed: {
    ring: 'ring-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-800',
    label: 'Confirmed', icon: CheckCircle2,
  },
  estimated: {
    ring: 'ring-amber-200', bg: 'bg-amber-50', text: 'text-amber-800',
    label: 'Estimated', icon: Info,
  },
  unknown: {
    ring: 'ring-slate-200', bg: 'bg-slate-50', text: 'text-slate-600',
    label: 'Unknown', icon: HelpCircle,
  },
};

export function ConfidenceBanner({ source, confidence, label, compact = false, className = '', variancePct = null, tolerancePct = 5 }) {
  if (!confidence && !source) return null;
  const tier = TIERS[confidence] || TIERS.unknown;
  const Icon = tier.icon;
  const srcText = SOURCE_LABELS[source] || source || '—';
  // S3 · Sheets Face-M² Bridge (2026-02c) — when the caller passes
  // `variancePct`, the banner tints amber/rose to signal Sheets ≠
  // Roof-Details divergence even if the underlying source is
  // confirmed. Green ≤ tolerance · amber ≤ 2× tolerance · rose >.
  const varianceActive = variancePct != null && variancePct > tolerancePct;
  const varianceTone = variancePct == null || variancePct <= tolerancePct
    ? null
    : (variancePct <= tolerancePct * 2
      ? { ring: 'ring-amber-300', bg: 'bg-amber-50', text: 'text-amber-900', label: 'Variance' }
      : { ring: 'ring-rose-300',  bg: 'bg-rose-50',  text: 'text-rose-900',  label: 'Variance' });
  const finalRing = varianceTone?.ring || tier.ring;
  const finalBg   = varianceTone?.bg   || tier.bg;
  const finalText = varianceTone?.text || tier.text;
  return (
    <span
      data-testid={`confidence-banner-${confidence || 'unknown'}${varianceActive ? '-variance' : ''}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${finalBg} ${finalText} ${finalRing} ${className}`}
      title={`${label ? label + ' · ' : ''}${tier.label} · ${srcText}${varianceActive ? ` · Sheets vs Roof-Details variance ${variancePct.toFixed(1)}% (tolerance ${tolerancePct}%)` : ''}`}
    >
      <Icon size={10} strokeWidth={2.5} />
      <span>{varianceActive ? `${tier.label} · ${variancePct.toFixed(1)}% Δ` : tier.label}</span>
      {!compact && <span className="font-normal opacity-70">· {srcText}</span>}
    </span>
  );
}
